<template>
  <b-row
    id="ContentEditor"
    class="m-0 overflow-hidden"
    :class="[showFooter ? 'tab-content-fill' : 'tab-content']"
  >
    <b-col
      class="p-0 h-100"
      v-if="showManageTrackersView"
    >
      <ManageTrackers @manageTrackersPreviousStep="manageTrackersPreviousStep" />
    </b-col>
    <template v-else>
      <b-col
        cols="4"
        class="bg-white p-0 border-right flex-fill h-100"
      >
        <div
          v-if="loadingArticlesSection"
          class="h-100 align-items-center d-flex justify-content-center card-body"
        >
          <div class="loader mx-auto" />
        </div>
        <ArticlesSection
          v-show="!loadingArticlesSection"
          ref="articleSection"
          data-test="articles-section"
        />
      </b-col>
      <b-col class="p-0 overflow-hidden h-100">
        <MultipleCreativeContentPreview
          v-if="showEditPanel"
          :creative-set="currentCreative"
          :content-item="contentItem"
          :read-only="readOnly"
          :view-type="viewType"
          @refreshContentList="refreshContentList()"
          @showManageTrackers="showManageTrackers()"
          ref="previewPanel"
        />
      </b-col>
    </template>
  </b-row>
</template>

<script>
import ArticlesSection from './Articles/ArticlesSection.vue';
import MultipleCreativeContentPreview from '@/modules/content/components/MultipleCreativeContentPreview';
import ManageTrackers from '@/modules/campaigns/components/ManageTrackers';
import {
  mapActions, mapState, mapGetters, mapMutations,
} from 'vuex';
import * as types from '@/modules/content/store/types';
import * as trackerTypes from '@/modules/trackers/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';


export default {
  name: 'ContentEditor',
  components: {
    MultipleCreativeContentPreview,
    ManageTrackers,
    ArticlesSection,
  },
  props: {
    showFooter: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Array,
      default: () => [],
    },
    viewType: {
      type: String,
      default: 'Editor',
    },
  },
  data() {
    return {
      selectedItem: '',
      pendingArticles: [],
      componentKey: 0,
      showManageTrackersView: false, // set to false after testing
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
      'selectedAdvertiser',
    ]),
    ...mapGetters('campaigns', {
      isManagedCampaign: campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      isCampaignReadonly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
      isViewType: campaignTypes.GET_IS_VIEW_TYPE,
    }),
    ...mapState('content', [
      'loadingArticlesSection',
      'selectedCreativeId',
      'feedId',
      'selectedArticleUuid',
    ]),
    ...mapState('content', {
      contentItems: 'newContentItems',
    }),
    ...mapGetters('content', {
      getArticles: types.GET_ARTICLES,
      templateOptions: types.GET_TEMPLATE_OPTIONS,
      currentCreative: types.GET_CURRENT_SELECTED_CREATIVE,
    }),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN,
    ]),
    isDraft() {
      return this.isViewType('Setup');
    },
    contentItem() {
      if (this.selectedItem === '' || !this.selectedArticleUuid) return {};
      return Array.from(this.contentItems)
        .find((item) => item.encodedExternalId === this.selectedArticleUuid) || {};
    },
    isCreativeSelected() {
      return this.selectedCreativeId !== 0;
    },
    showEditPanel() {
      if (Object.keys(this.contentItem).length === 0) {
        return false;
      }
      return this.selectedItem !== '';
    },
  },
  methods: {
    ...mapActions('content', [
      types.SCRAPED_CONTENT,
      types.FETCH_SHARED_COUNT,
      types.FETCH_FILTERED_FEED_CONTENT_ITEMS,
      types.FETCH_CONTENT_DISPLAY_COPY,
      types.FETCH_ARTICLES,
      types.FETCH_CONTENT_COMMENTS_COUNT,
    ]),
    ...mapActions('trackers', [
      trackerTypes.FETCH_SELF_SERVICE_CONTENT_TRACKERS,
    ]),
    ...mapMutations('content', [
      types.SET_LOADING_ARTICLES_SECTION,
    ]),
    ...mapActions('campaigns', {
      getProperties: campaignTypes.GET_CAMPAIGN_PROPERTIES,
    }),
    async setContentItem(id) {
      this.selectedItem = this.selectedArticleUuid ? this.selectedArticleUuid : id;
      if (this.$refs.editPanel) {
        this.$refs.editPanel.setTimestamp();
      }
    },
    fetchContentTrackers() {
      const { advertiserId } = this.$route.params;
      this.FETCH_SELF_SERVICE_CONTENT_TRACKERS({ advertiserId, creativeId: this.currentCreative.id });
    },
    async refreshContentList() {
      await this.FETCH_ARTICLES();
      await this.FETCH_CONTENT_COMMENTS_COUNT(this.selectedCreativeId);
      await this.FETCH_FILTERED_FEED_CONTENT_ITEMS({ feedId: this.feedId, allContent: this.isDraft });
      this.pendingArticles = this.getArticles(this.isDraft);
    },
    async handleCreate() {
      await this.refreshContentList();
      if (this.pendingArticles.length > 0) {
        if ((!this.selectedItem)) {
          this.setContentItem(this.pendingArticles[0].encodedExternalId);
        } else {
          const currentSelectedItem = this.getArticles(this.isDraft)
            .find((item) => item.encodedExternalId === this.selectedItem);
          if (currentSelectedItem) this.setContentItem(currentSelectedItem.encodedExternalId);
          else this.setContentItem(this.pendingArticles[0].encodedExternalId);
        }
        if (!this.isManagedCampaign && this.isCreativeSelected) {
          this.fetchContentTrackers();
        }
      }
    },
    doArticlesContainInvalidContentType() {
      if (this.contentItems.length === 0) {
        return false;
      }

      const contentItemContainsInvalidType = this.contentItems.some((contentItem) => contentItem.contentItemProperties
        && contentItem.contentItemProperties.contentType
        && !contentItem.contentItemProperties.contentType.includes('html'));
      this.$emit('setNotValidForNavigation', 'next', contentItemContainsInvalidType);
    },
    showManageTrackers() {
      this.showManageTrackersView = true;
    },
    manageTrackersPreviousStep() {
      this.showManageTrackersView = false;
    },
  },
  async created() {
    this.SET_LOADING_ARTICLES_SECTION(true);
    const { selectedItem } = this.$route.query;
    if (selectedItem) this.selectedItem = selectedItem;
    await this.handleCreate();
    await this.doArticlesContainInvalidContentType();
    const { advertiserId, campaignId } = this.$route.params;
    const payload = {
      advertiserId: advertiserId ?? this.campaign.advertiserId,
      campaignId: campaignId ?? this.campaign.id,
      type: this.type,
    };
    this.getProperties(payload);
  },
  async mounted() {
    await this.FETCH_CONTENT_DISPLAY_COPY(!this.isDraft);
  },
};
</script>
<style lang="scss">
.tab-content {
  height: calc(100% - 54px);
  overflow: hidden;
}

.tab-content-fill {
  height: calc(100% - 65px);
  padding-bottom: 69px;
}
</style>
